const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

// Mock auth token for development - in production this would come from auth context
const getAuthToken = () => {
  return 'mock-admin-token';
};

const headers = {
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getAuthToken()}`,
};

export interface User {
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone: string;
  role: 'admin' | 'doctor' | 'patient' | 'family' | 'hospital_staff' | 'ambulance_crew';
  status?: 'active' | 'inactive' | 'pending';
  is_active?: boolean;
  institutionId?: string;
  institutionName?: string;
  specialization?: string;
  licenseNumber?: string;
  license_number?: string;
  created_at?: string;
  createdAt?: string;
}

export interface Institution {
  id: string;
  name: string;
  type: 'hospital' | 'clinic' | 'ambulance_service';
  address: string;
  phone: string;
  email: string;
  capacity?: number;
  specialties?: string[];
  status: 'active' | 'inactive';
  region: string;
  comuna: string;
  rut?: string;
  codigo_deis?: string;
}

export interface CreateUserRequest {
  email: string;
  phone: string;
  password: string;
  role: User['role'];
  first_name: string;
  last_name: string;
  date_of_birth: string;
  national_id: string;
  gender?: 'male' | 'female' | 'other';
  address?: string;
  city?: string;
  institution_id?: string;
  specialization?: string;
  license_number?: string;
}

export interface CreateInstitutionRequest {
  name: string;
  type: Institution['type'];
  address: string;
  region: string;
  comuna: string;
  phone: string;
  email: string;
  rut?: string;
  codigo_deis?: string;
  capacity?: number;
  specialties?: string[];
}

class AdminApiService {
  // Users API
  async getUsers(filters?: {
    role?: User['role'];
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: User[], pagination?: any }> {
    try {
      const params = new URLSearchParams();
      if (filters?.role) params.append('role', filters.role);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await fetch(`${API_BASE_URL}/admin/users?${params}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Transform the data to match our interface
      const transformedUsers = result.data.map((user: any): User => ({
        id: user.id,
        name: user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.name,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone,
        role: user.role,
        status: user.is_active ? 'active' : 'inactive',
        is_active: user.is_active,
        institutionId: user.institution_id,
        institutionName: user.institution_name,
        specialization: user.specialization,
        licenseNumber: user.license_number,
        created_at: user.created_at,
        createdAt: user.created_at,
      }));

      return {
        data: transformedUsers,
        pagination: result.pagination,
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      // Return mock data for development
      return {
        data: [
          {
            id: '1',
            name: 'Dr. María González',
            email: 'maria.gonzalez@hospital.cl',
            phone: '+56912345678',
            role: 'doctor',
            status: 'active',
            institutionId: 'h1',
            institutionName: 'Hospital Regional Chillán',
            specialization: 'Cardiología',
            licenseNumber: 'MD-12345',
            created_at: '2024-01-15'
          },
          {
            id: '2',
            name: 'Juan Pérez',
            email: 'juan.perez@gmail.com',
            phone: '+56987654321',
            role: 'patient',
            status: 'active',
            created_at: '2024-02-10'
          },
          {
            id: '3',
            name: 'Ana Contreras',
            email: 'ana.contreras@ambulancia.cl',
            phone: '+56955555555',
            role: 'ambulance_crew',
            status: 'active',
            institutionId: 'a1',
            institutionName: 'SAMU Ñuble',
            created_at: '2024-01-20'
          }
        ] as User[],
      };
    }
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers,
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<User> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Institutions API
  async getInstitutions(filters?: {
    type?: Institution['type'];
    region?: string;
    search?: string;
  }): Promise<Institution[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.region) params.append('region', filters.region);
      if (filters?.search) params.append('search', filters.search);

      const response = await fetch(`${API_BASE_URL}/admin/institutions?${params}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching institutions:', error);
      // Return mock data for development
      return [
        {
          id: 'h1',
          name: 'Hospital Regional Chillán',
          type: 'hospital',
          address: 'Av. Argentina 1234, Chillán',
          phone: '+56422234567',
          email: 'contacto@hospitalchillan.cl',
          capacity: 200,
          specialties: ['Cardiología', 'Neurología', 'Emergencia'],
          status: 'active',
          region: 'Ñuble',
          comuna: 'Chillán'
        },
        {
          id: 'a1',
          name: 'SAMU Ñuble',
          type: 'ambulance_service',
          address: 'Calle Central 567, Chillán',
          phone: '+56131',
          email: 'coordinacion@samu.cl',
          status: 'active',
          region: 'Ñuble',
          comuna: 'Chillán'
        }
      ] as Institution[];
    }
  }

  async createInstitution(institutionData: CreateInstitutionRequest): Promise<Institution> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/institutions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(institutionData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error creating institution:', error);
      throw error;
    }
  }

  async updateInstitution(institutionId: string, institutionData: Partial<Institution>): Promise<Institution> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/institutions/${institutionId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(institutionData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error updating institution:', error);
      throw error;
    }
  }

  async deleteInstitution(institutionId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/institutions/${institutionId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting institution:', error);
      throw error;
    }
  }

  // System stats
  async getStats(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/stats`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Return mock stats for development
      return {
        users: {
          total: 3,
          patients: 1,
          doctors: 1,
          nurses: 0,
          admins: 1,
        },
        institutions: {
          total: 2,
          by_type: {
            hospital: 1,
            clinic: 0,
            ambulance_service: 1,
          },
          active: 2,
          inactive: 0,
        },
        emergencies: {
          today: 24,
          this_week: 156,
          this_month: 642,
          average_response_time: 8.5,
        },
      };
    }
  }
}

export const adminApiService = new AdminApiService();
export default adminApiService;